import {
  DateFormat,
  formatDate,
  formatName,
  IconFolderPlus,
  pluralize,
  ProfileImage,
  ProfileImageStack,
  Rating,
  RatingSize,
  RatingTextColor,
  Tag,
  TagColor,
  TagRounding,
  TagSize,
} from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import Link from 'next/link';
import { type FC } from 'react';

import phog from '../../utils/analytics/posthog/posthog';
import { getBootcampUrl } from '../../utils/bootcamps';
import { getCoachUrl } from '../../utils/coach';
import { BootcampUrgencyTag } from '../bootcamp/BootcampUrgencyTag';

import {
  type BootcampCardBootcampFragment,
  type BootcampCardCategoryFragment,
} from './__generated-gql-types__/BootcampCard.generated';

interface BootcampCardProps {
  bootcamp: BootcampCardBootcampFragment;
  category: Possible<BootcampCardCategoryFragment>;
  source?: string;
}

export const BootcampCard: FC<BootcampCardProps> = ({
  bootcamp,
  category,
  source,
}) => {
  const categorySlug = category?.slug;
  const nextCohort = bootcamp.cohorts.find(
    (cohort) => cohort.sessions[0].startAt > Date.now(),
  );
  return (
    <Link
      href={getBootcampUrl(bootcamp.slug)}
      className="flex flex-col justify-between gap-3 rounded-lg border p-5 pb-0 shadow-md"
      id="bootcamp_srp--card"
      onClick={() => {
        phog.capture('bootcamp_card--click', {
          bootcamp_id: bootcamp.id,
          source,
        });
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-5 lg:min-h-28 lg:flex-row">
          {bootcamp.imageLink ? (
            <div className="relative aspect-video h-28 w-fit">
              <Image
                src={bootcamp.imageLink}
                alt={bootcamp.title}
                fill
                className="rounded-md object-cover"
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <h3 className="text-3xl font-medium">{bootcamp.title}</h3>
            <div className="flex flex-wrap gap-1.5 uppercase leading-4 tracking-wider">
              {bootcamp.curriculums.length > 0 ? (
                <Tag
                  text={`${pluralize(bootcamp.curriculums.length, 'session')}`}
                  size={TagSize.XS}
                  tagColor={TagColor.GRAY}
                  rounding={TagRounding.SM}
                />
              ) : null}

              {nextCohort ? (
                <div className="">
                  <Tag
                    text={`Next cohort starts ${formatDate(nextCohort.sessions[0].startAt, DateFormat.SHORT_MONTH_DAY)}`}
                    size={TagSize.XS}
                    tagColor={TagColor.GRAY}
                    rounding={TagRounding.SM}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <p
          className={`line-clamp-2 text-lg text-leland-gray-light ${bootcamp.headline ? '' : 'flex-1'}`}
        >
          {bootcamp.headline}
        </p>
        <div className="flex gap-3">
          {bootcamp.coaches.length > 0 ? (
            <div className="flex items-center">
              <ProfileImageStack
                profiles={[
                  ...bootcamp.coaches.slice(0, 4).map((coach) => ({
                    firstName: coach.user.firstName,
                    lastName: coach.user.lastName,
                    pictureLink: coach.user.pictureLink,
                  })),
                ]}
                additionalProfiles={bootcamp.coaches.length - 4}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ProfileImage firstName="" lastName="" className="size-5" />
              <span className="font-medium">Hosted by Expert Coaches</span>
            </div>
          )}

          <div>
            {bootcamp.coaches.length <= 2 ? (
              <div className="font-medium">
                {bootcamp.coaches.map((coach, index) => (
                  <>
                    {index === 1 ? <span> and </span> : null}
                    <Link
                      href={getCoachUrl({
                        coachSlug: coach.slug,
                        categorySlug,
                      })}
                      className="underline"
                      key={coach.id}
                      id="bootcamp_srp--coach_link"
                    >
                      {formatName({
                        firstName: coach.user.firstName,
                        lastName: coach.user.lastName,
                      })}
                    </Link>
                  </>
                ))}
              </div>
            ) : (
              <div className="font-medium">
                <Link
                  href={getCoachUrl({
                    coachSlug: bootcamp.coaches[0].slug,
                    categorySlug,
                  })}
                  className="underline"
                  key={bootcamp.coaches[0].id}
                  id="bootcamp_srp--coach_link"
                >
                  {formatName({
                    firstName: bootcamp.coaches[0].user.firstName,
                    lastName: bootcamp.coaches[0].user.lastName,
                  })}
                </Link>
                ,{' '}
                <Link
                  href={getCoachUrl({
                    coachSlug: bootcamp.coaches[1].slug,
                    categorySlug,
                  })}
                  className="underline"
                  key={bootcamp.coaches[1].id}
                  id="bootcamp_srp--coach_link"
                >
                  {formatName({
                    firstName: bootcamp.coaches[1].user.firstName,
                    lastName: bootcamp.coaches[1].user.lastName,
                  })}
                </Link>{' '}
                and {bootcamp.coaches.length - 2} more expert coaches
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="flex flex-col justify-between gap-1.5 border-t py-3 lg:flex-row lg:items-center lg:gap-0">
          {bootcamp.reviewRate ? (
            <div className="flex items-center gap-2 font-medium uppercase tracking-wider">
              <Rating
                rate={bootcamp.reviewRate}
                size={RatingSize.XS}
                showRate
                textColor={RatingTextColor.DARK}
              />

              <p className="text-sm uppercase text-leland-gray-extra-light">
                {bootcamp.reviewCount} reviews
              </p>
            </div>
          ) : null}
          <BootcampUrgencyTag
            seatsLeft={
              bootcamp.maximumAttendees && nextCohort
                ? bootcamp.maximumAttendees - nextCohort.totalAttendees
                : null
            }
            startAt={nextCohort?.sessions[0]?.startAt}
            fallback={
              <div className="flex items-center gap-1 text-sm font-medium uppercase tracking-wider text-leland-gray-extra-light">
                <IconFolderPlus className="size-4" /> Includes Leland+ Access
              </div>
            }
            noTag
          />
        </div>
      </div>
    </Link>
  );
};
